/**
 * Free Shipping Progress Bar
 *
 * Visual progress indicator showing how close the user is to the free shipping
 * threshold. Three visual states:
 *   - hidden:   threshold not configured (isEnabled=false)
 *   - progress: cart is below threshold — shows fill bar + "You're $X away"
 *   - unlocked: cart >= threshold — full bar + success message
 */

'use client';

import { formatPrice, type FormatPriceOptions, SafeHtml } from '@template-project/afrodita-components';
import { useTranslation } from '@/lib/i18n';
import {
  CART_FREE_SHIPPING_PROGRESS_LABEL,
  CART_FREE_SHIPPING_PROGRESS_PREFIX,
  CART_FREE_SHIPPING_PROGRESS_SUFFIX,
  CART_FREE_SHIPPING_UNLOCKED,
} from './i18.keys';
import {
  ProgressBarContainer,
  ProgressBarTrack,
  ProgressBarFill,
  ProgressBarLabels,
  ProgressBarLabel,
  ProgressBarMessage,
  ProgressBarMessageBold,
} from './CartSummaryV2.styled';
import type { FreeShippingState } from '@/lib/hooks/use-free-shipping';

export interface FreeShippingProgressBarProps {
  freeShipping: FreeShippingState;
  priceOptions: FormatPriceOptions;
}

export function FreeShippingProgressBar({
  freeShipping,
  priceOptions,
}: Readonly<FreeShippingProgressBarProps>) {
  const { t } = useTranslation('sidecart');

  if (!freeShipping.isEnabled) {
    return null;
  }

  const currentFormatted = formatPrice(
    freeShipping.threshold - freeShipping.remaining,
    priceOptions,
  );
  const thresholdFormatted = formatPrice(freeShipping.threshold, priceOptions);
  const remainingFormatted = formatPrice(freeShipping.remaining, priceOptions);

  if (freeShipping.isUnlocked) {
    return (
      <ProgressBarContainer data-testid="free-shipping-bar">
        <ProgressBarTrack>
          <ProgressBarFill $percent={100} $unlocked />
        </ProgressBarTrack>
        <ProgressBarLabels>
          <ProgressBarLabel>{currentFormatted}</ProgressBarLabel>
          <ProgressBarLabel>{thresholdFormatted}</ProgressBarLabel>
        </ProgressBarLabels>
        <SafeHtml
          as={ProgressBarMessage}
          html={t(CART_FREE_SHIPPING_UNLOCKED)}
          data-testid="free-shipping-unlocked"
        />
      </ProgressBarContainer>
    );
  }

  return (
    <ProgressBarContainer data-testid="free-shipping-bar">
      <ProgressBarTrack>
        <ProgressBarFill $percent={freeShipping.percentage} />
      </ProgressBarTrack>
      <ProgressBarLabels>
        <ProgressBarLabel>{currentFormatted}</ProgressBarLabel>
        <ProgressBarLabel>{thresholdFormatted}</ProgressBarLabel>
      </ProgressBarLabels>
      <ProgressBarMessage data-testid="free-shipping-progress">
        {t(CART_FREE_SHIPPING_PROGRESS_PREFIX)}{' '}
        <ProgressBarMessageBold>{remainingFormatted}</ProgressBarMessageBold>{' '}
        {t(CART_FREE_SHIPPING_PROGRESS_SUFFIX)}{' '}
        <ProgressBarMessageBold>{t(CART_FREE_SHIPPING_PROGRESS_LABEL)}</ProgressBarMessageBold>
      </ProgressBarMessage>
    </ProgressBarContainer>
  );
}
